import React, { useCallback, useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Info, Download, FileCheck, ChevronDown, ChevronRight } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { downloadInvoicePdf } from '../utils/invoicePdf';

// ── Constants ─────────────────────────────────────────────────────────────────

const BILLING_PERIOD_LABELS = { monthly: 'Månadsvis', quarterly: 'Kvartalsvis', annual: 'Årsvis' };

const STATUS_META = {
  skickad:    { label: 'Skickad',    cls: 'bg-blue-100 text-blue-700' },
  betald:     { label: 'Betald',     cls: 'bg-green-100 text-green-700' },
  förfallen:  { label: 'Förfallen',  cls: 'bg-red-100 text-red-700' },
  krediterad: { label: 'Krediterad', cls: 'bg-yellow-100 text-yellow-700' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(val) {
  const n = parseFloat(val) || 0;
  return n.toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' SEK';
}

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('sv-SE') : '–';
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status, cls: 'bg-gray-100 text-gray-700' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${m.cls}`}>
      {m.label}
    </span>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const res = await api.get('/org/invoices', { params });
      setInvoices(res.data);
    } catch {
      toast.error('Kunde inte hämta fakturor');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleDownload = async (inv) => {
    setDownloading(inv.id);
    try {
      const res = await api.get(`/org/invoices/${inv.id}`);
      await downloadInvoicePdf(res.data);
    } catch {
      toast.error('Kunde inte ladda ner faktura');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto">

        {/* Header */}
        <div>
          <h1
            className="text-2xl font-black"
            data-testid="invoices-heading"
            style={{ color: '#1e293b' }}
          >
            Fakturor
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Dina fakturor från AIUS</p>
        </div>

        {/* Info banner */}
        <div
          className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4"
          data-testid="info-banner"
        >
          <Info size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-800">
            Kontakta din administratör vid frågor om fakturering.
          </p>
        </div>

        {/* Filter */}
        <div className="flex gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <select
              className="h-9 px-3 border border-input rounded-md bg-background text-sm focus:outline-none"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              data-testid="filter-status"
            >
              <option value="">Alla statusar</option>
              {Object.entries(STATUS_META).map(([v, m]) => (
                <option key={v} value={v}>{m.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <Card className="shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-10 text-center text-muted-foreground text-sm" data-testid="loading-state">
                Laddar fakturor…
              </div>
            ) : invoices.length === 0 ? (
              <div className="p-10 text-center space-y-2" data-testid="empty-state">
                <FileCheck size={40} className="mx-auto text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">Inga fakturor att visa</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="invoices-table">
                  <thead>
                    <tr className="border-b" style={{ backgroundColor: '#f8fafc' }}>
                      <th className="w-8 px-3 py-3" />
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Fakturanr</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Period</th>
                      <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Totalt</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">Förfaller</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <React.Fragment key={inv.id}>
                        <tr
                          className="border-b hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={() => setExpandedId(expandedId === inv.id ? null : inv.id)}
                          data-testid={`invoice-row-${inv.id}`}
                        >
                          <td className="px-3 py-3 text-muted-foreground">
                            {expandedId === inv.id
                              ? <ChevronDown size={14} />
                              : <ChevronRight size={14} />}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs font-semibold" style={{ color: '#1e293b' }}>
                            {inv.invoice_number}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{BILLING_PERIOD_LABELS[inv.billing_period] || inv.billing_period}</td>
                          <td className="px-4 py-3 text-right font-semibold">{fmt(inv.total_amount)}</td>
                          <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                          <td className="px-4 py-3 text-muted-foreground">{fmtDate(inv.due_date)}</td>
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="outline"
                              size="sm"
                              data-testid={`download-${inv.id}`}
                              disabled={downloading === inv.id}
                              onClick={() => handleDownload(inv)}
                              className="rounded-full"
                            >
                              <Download size={13} className="mr-1.5" />
                              {downloading === inv.id ? 'Laddar…' : 'Ladda ner'}
                            </Button>
                          </td>
                        </tr>
                        {expandedId === inv.id && (
                          <tr className="bg-slate-50/70">
                            <td colSpan={7} className="px-6 py-3">
                              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Rader</p>
                              {inv.lines?.length ? (
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-muted-foreground">
                                      <th className="text-left py-1 font-medium">Beskrivning</th>
                                      <th className="text-center py-1 font-medium">Antal</th>
                                      <th className="text-right py-1 font-medium">À-pris</th>
                                      <th className="text-right py-1 font-medium">Belopp</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {inv.lines.map((l, li) => (
                                      <tr key={li}>
                                        <td className="py-0.5">{l.description}</td>
                                        <td className="py-0.5 text-center">{l.quantity}</td>
                                        <td className="py-0.5 text-right">{fmt(l.unit_price)}</td>
                                        <td className="py-0.5 text-right">{fmt(l.amount)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <p className="text-xs text-muted-foreground">Inga rader</p>
                              )}
                              {inv.description && (
                                <p className="text-xs text-muted-foreground mt-2">{inv.description}</p>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
